import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../../classes/base-component';
import { ApplicationService } from '../../services/application.service';
import { DownloadService } from '../../services/download.service';

@Component({
    selector: 'app-downloads',
    templateUrl: './downloads.component.html',
    styleUrls: ['./downloads.component.scss'],
})
export class DownloadsComponent extends BaseComponent implements OnInit {
    selectedMonth = '';
    availableMonths: Array<{ value: string; label: string }> = [];
    isEnergyReportLoading = false;
    isExportLoading = false;
    isThirdPartyExportLoading = false;
    isGdprExportLoading = false;

    constructor(
        private title: Title,
        private toastr: ToastrService,
        private translate: TranslateService,
        private downloadService: DownloadService,
        public application: ApplicationService
    ) {
        super();
    }

    ngOnInit() {
        this.title.setTitle('Meine Downloads | E.ON Smart Control');

        this.loadAvailableMonths();

        this.addSub(
            this.translate.onLangChange.subscribe(() => {
                this.loadAvailableMonths();
            })
        );
    }

    loadAvailableMonths() {
        this.downloadService.getTranslatedMonths().subscribe((months) => {
            this.availableMonths = months;
            if (this.availableMonths.length > 0) {
                this.selectedMonth = this.availableMonths[0].value;
            }
        });
    }

    private getPreviousMonth(): string {
        const currentDate = new Date();
        const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const year = previousMonth.getFullYear();
        const month = String(previousMonth.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }

    openEnergyReport() {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return;
        }

        this.isEnergyReportLoading = true;
        this.downloadService.openEnergyReport(this.getPreviousMonth());

        // Subscribe to the loading state
        this.addSub(
            this.downloadService.isEnergyReportLoading$.subscribe((loading) => {
                this.isEnergyReportLoading = loading;
            })
        );
    }

    exportConsumptionData(): void {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return;
        }

        if (!this.selectedMonth) {
            this.toastr.error(this.translate.instant('screens.downloads.export.selectMonthError'));
            return;
        }

        this.isExportLoading = true;

        this.downloadService.exportConsumptionData(this.selectedMonth).subscribe({
            next: (response: Blob) => {
                if (!response) {
                    this.toastr.error(this.translate.instant('common.errors.generalError'));
                    this.isExportLoading = false;
                    return;
                }

                const blobUrl = window.URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `stromverbrauch_${this.selectedMonth}.csv`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);

                this.isExportLoading = false;
            },
            error: (err) => {
                console.error('Export error:', err);
                this.isExportLoading = false;
            },
        });
    }

    exportToThirdParties(): void {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return;
        }

        this.isThirdPartyExportLoading = true;

        this.downloadService.exportToThirdParties().subscribe({
            next: (res) => {
                if (res.status === 'accepted') {
                    this.toastr.warning(
                        this.translate.instant('screens.downloads.exportToThirdParties.cooldown'),
                        '',
                        {
                            timeOut: 10000,
                            closeButton: true,
                        }
                    );
                } else {
                    this.toastr.success(
                        this.translate.instant('screens.downloads.exportToThirdParties.success'),
                        '',
                        {
                            timeOut: 10000,
                            closeButton: true,
                        }
                    );
                }
                this.isThirdPartyExportLoading = false;
            },
            error: (err) => {
                this.isThirdPartyExportLoading = false;
                console.error(err);
                this.toastr.error(
                    this.translate.instant('screens.downloads.exportToThirdParties.error')
                );
            },
        });
    }

    exportGdprData(): void {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return;
        }

        this.isGdprExportLoading = true;

        this.downloadService.exportGdprData().subscribe({
            next: (res) => {
                if (res.status === 'accepted') {
                    this.toastr.warning(
                        this.translate.instant('screens.downloads.gdprExport.cooldown'),
                        '',
                        {
                            timeOut: 10000,
                            closeButton: true,
                        }
                    );
                } else {
                    this.toastr.success(
                        this.translate.instant('screens.downloads.gdprExport.success'),
                        '',
                        {
                            timeOut: 10000,
                            closeButton: true,
                        }
                    );
                }
                this.isGdprExportLoading = false;
            },
            error: (err) => {
                this.isGdprExportLoading = false;
                console.error(err);
                this.toastr.error(this.translate.instant('screens.downloads.gdprExport.error'));
            },
        });
    }
}
